import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import XLSX from 'xlsx'

import useVirtualRows from 'pages/parts/useVirtualRows'
import { httpOk, POST } from 'helpers/api'
import { formatInventoryQty } from 'helpers/helpers'

// Высота строки таблицы — должна совпадать с .doc-table tbody td в style.css
const ROW_HEIGHT = 33

function withInventoryQty(item) {
	const balance = Number(item.balance || 0)
	const actual = Number(item.actual_balance ?? item.balance ?? 0)
	const price = Number(item.price || 0)
	const divergence = actual - balance
	return {
		...item,
		balance,
		price,
		actual_balance: formatInventoryQty(actual),
		divergence,
		difference_amount: divergence * price,
	}
}

function Index({ data, setData, modalNotInventored, setModalNotInventored }) {
	const { t } = useTranslation();

	const [annual, setAnnual] = useState(false)
	const [search, setSearch] = useState('')
	const [notInventoredProducts, setNotInventoredProducts] = useState([])

	// Держим исходный индекс: обработчики (чекбокс, посчитано, плюс) работают
	// по позиции в notInventoredProducts, а не в отфильтрованном списке.
	const filteredProducts = useMemo(() => {
		var indexedProducts = notInventoredProducts.map((item, index) => ({ item, index }))
		var searchValue = search.trim().toLowerCase()

		if (!searchValue) return indexedProducts

		return indexedProducts.filter(({ item }) =>
			String(item.name ?? '').toLowerCase().includes(searchValue))
	}, [notInventoredProducts, search])

	// Список не инвентаризованных бывает на тысячи позиций — рисуем только видимые
	const virtual = useVirtualRows({
		count: filteredProducts.length,
		rowHeight: ROW_HEIGHT,
	})

	function addToListMinusBalanceProducts() {
		var notInventoredProductsCopy = [...notInventoredProducts]
		var dataCopy = { ...data }

		var minusProducts = notInventoredProductsCopy
			.filter(element => Number(element.balance) < 0)
			.map(withInventoryQty)
		dataCopy.elements = minusProducts

		setData(dataCopy)
		setModalNotInventored(false)
	}

	function addToListNotInventoredProduct(index) {
		var notInventoredProductsCopy = [...notInventoredProducts]
		var dataCopy = { ...data }

		dataCopy.elements.push(withInventoryQty(notInventoredProductsCopy[index]))
		notInventoredProductsCopy.splice(index, 1)

		setNotInventoredProducts(notInventoredProductsCopy)

		if (notInventoredProductsCopy.length === 0) {
			setModalNotInventored(false)
		}
	}

	function addToListNotInventoredProducts() {
		var dataCopy = { ...data }
		var notInventoredProductsCopy = notInventoredProducts.map(withInventoryQty)

		dataCopy.elements = dataCopy.elements.concat(notInventoredProductsCopy)

		setData(dataCopy)
		setModalNotInventored(false)
	}

	function setActualBalanceModal(e, index) {
		var notInventoredProductsCopy = [...notInventoredProducts]
		notInventoredProductsCopy[index]['actual_balance'] = e.target.value
		setNotInventoredProducts(notInventoredProductsCopy)
	}

	function addToListSelectedProducts() {
		var notInventoredProductsCopy = [...notInventoredProducts]
		var dataCopy = { ...data }

		for (let i = 0; i < notInventoredProductsCopy.length; i++) {
			if (notInventoredProductsCopy[i]['selected']) {
				dataCopy.elements.push(withInventoryQty(notInventoredProductsCopy[i]))
			}
		}

		setData(dataCopy)
		setModalNotInventored(false)
	}

	function markAsSelected(bool, index) {
		var notInventoredProductsCopy = [...notInventoredProducts]
		notInventoredProductsCopy[index]['selected'] = bool

		setNotInventoredProducts(notInventoredProductsCopy)
	}

	function sortBy(key, orderBy) {
		var notInventoredProductsCopy = [...notInventoredProducts]
		if (key === 'balance' && orderBy === 'asc') {
			notInventoredProductsCopy.sort((a, b) => parseFloat(a.balance) - parseFloat(b.balance));
		}
		if (key === 'balance' && orderBy === 'desc') {
			notInventoredProductsCopy.sort((a, b) => parseFloat(b.balance) - parseFloat(a.balance));
		}
		if (key === 'name' && orderBy === 'asc') {
			notInventoredProductsCopy.sort((a, b) => String(a.name ?? '').localeCompare(String(b.name ?? '')))
		}
		if (key === 'name' && orderBy === 'desc') {
			notInventoredProductsCopy.sort((a, b) => String(b.name ?? '').localeCompare(String(a.name ?? '')))
		}

		setNotInventoredProducts(notInventoredProductsCopy)
	}

	async function exportToExcel() {
		var excelHeaders = [[t('product_name'), t('price'), t('expected_balance')]]
		for (let i = 0; i < notInventoredProducts.length; i++) {
			var excelItems = []
			excelItems.push(notInventoredProducts[i]['name'])
			excelItems.push(notInventoredProducts[i]['price'])
			excelItems.push(notInventoredProducts[i]['balance'])
			excelHeaders.push(excelItems)
		}

		const ws = XLSX.utils.aoa_to_sheet(excelHeaders);
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, "SheetJS");
		XLSX.writeFile(wb, t('not_inventored') + ".xlsx");
	}

	async function notInventoredList() {
		var arrayIds = []
		for (let i = 0; i < data.elements.length; i++) {
			arrayIds.push(data.elements[i]['element_id'])
		}
		var sendData = {
			"balance_ids": [],
			"pos_id": data.pos_id,
			"wms_id": data.wms_id
		}
		sendData.balance_ids = arrayIds

		const response = await POST('/inventory/not-inventored', sendData, { loader: true })
		if (httpOk(response)) {
			var list = [...response.data]
			for (let i = 0; i < list.length; i++) {
				list[i]['selected'] = false
				list[i]['actual_balance'] = formatInventoryQty(list[i]['balance'])
			}

			setSearch('')
			setNotInventoredProducts(list)
			setModalNotInventored(true)
		}
	}

	function annualActualBalance() {
		var notInventoredProductsCopy = [...notInventoredProducts]
		if (annual) {
			var selectedExist = false

			for (let i = 0; i < notInventoredProductsCopy.length; i++) {
				if (notInventoredProductsCopy[i]['selected']) {
					selectedExist = true
					break;
				}
			}

			if (selectedExist) {
				for (let i = 0; i < notInventoredProductsCopy.length; i++) {
					if (notInventoredProductsCopy[i]['selected']) {
						notInventoredProductsCopy[i]['actual_balance'] = 0
					}
				}
			} else {
				for (let i = 0; i < notInventoredProductsCopy.length; i++) {
					notInventoredProductsCopy[i]['actual_balance'] = 0
				}
			}
		} else {
			for (let i = 0; i < notInventoredProductsCopy.length; i++) {
				notInventoredProductsCopy[i]['actual_balance'] = formatInventoryQty(notInventoredProductsCopy[i]['balance'])
			}
		}

		setNotInventoredProducts(notInventoredProductsCopy)
	}

	function toggleModal(bool = false) {
		setSearch('')
		setNotInventoredProducts([])
		setModalNotInventored(bool)
	}

	useEffect(() => {
		annualActualBalance()
	}, [annual]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (modalNotInventored) notInventoredList()
	}, [modalNotInventored]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			{/* MODAL NOT INVENTORED PRODUCTS */}
			<Modal show={modalNotInventored} animation={false} centered size="lg"
				onHide={() => toggleModal(false)}>
				<Modal.Header closeButton>
					<Modal.Title>
						<div className="d-flex gap-2">
							<div className="me-4">{t('annual')}</div>
							<div className="form-check form-switch form-switch-lg">
								<input type="checkbox" className="form-check-input"
									defaultValue={false} onChange={(e) => setAnnual(e.target.checked)} />
							</div>
							<div>{t('sort_by')}</div>
							<button className="btn btn-sm btn-success" onClick={() => sortBy('balance', 'asc')}>[0-9]</button>
							<button className="btn btn-sm btn-success" onClick={() => sortBy('balance', 'desc')}>[9-0]</button>
							<button className="btn btn-sm btn-success" onClick={() => sortBy('name', 'asc')}>[A-Z]</button>
							<button className="btn btn-sm btn-success" onClick={() => sortBy('name', 'desc')}>[Z-A]</button>
						</div>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="table-responsive inventory-modal-scroll" ref={virtual.containerRef}>
						<table className="table table-bordered doc-table mb-0" style={{ 'minWidth': '620px' }}>
							<colgroup>
								<col style={{ 'width': '40px' }} />
								<col />
								<col style={{ 'width': '20%' }} />
								<col style={{ 'width': '110px' }} />
								<col style={{ 'width': '120px' }} />
								<col style={{ 'width': '40px' }} />
							</colgroup>
							<thead>
								<tr>
									<th className="text-center">#</th>
									<th className="th-search">
										<input
											type="text"
											name="search"
											autoComplete="off"
											placeholder={t('search')}
											value={search}
											onChange={(e) => setSearch(e.target.value)} />
									</th>
									<th>{t('category')}</th>
									<th className="text-end">{t('balance2')}</th>
									<th className="text-end">{t('counted')}</th>
									<th className="text-end"></th>
								</tr>
							</thead>
							<tbody>
								{virtual.topSpacer > 0 &&
									<tr className="doc-row-spacer" aria-hidden="true">
										<td colSpan={6} style={{ 'height': `${virtual.topSpacer}px` }}></td>
									</tr>
								}
								{filteredProducts.slice(virtual.start, virtual.end).map(({ item, index }) => (
									<tr key={index}>
										<td>
											<input type="checkbox"
												id={`control${index}`}
												onChange={(e) => markAsSelected(e.target.checked, index)} />
										</td>
										<td>{item.name}</td>
										<td>{item.category_name}</td>
										<td className="text-end">{formatInventoryQty(item.balance)}</td>
										<td className="td-input">
											<input
												type="number"
												name="actual_balance"
												autoComplete="off"
												className="form-control text-end"
												placeholder="0"
												value={item.actual_balance ?? ''}
												onChange={(e) => setActualBalanceModal(e, index)} />
										</td>
										<td>
											<div className="table-action-button table-action-primary-button"
												onClick={() => addToListNotInventoredProduct(index)}>
												<i className="uil-plus"></i>
											</div>
										</td>
									</tr>
								))}
								{virtual.bottomSpacer > 0 &&
									<tr className="doc-row-spacer" aria-hidden="true">
										<td colSpan={6} style={{ 'height': `${virtual.bottomSpacer}px` }}></td>
									</tr>
								}
							</tbody>
						</table>
					</div>
				</Modal.Body>
				<Modal.Footer>
					<button className="btn btn-secondary" tabIndex="-1"
						onClick={() => exportToExcel()}>
						EXCEL
					</button>
					<button className="btn btn-info" tabIndex="-1"
						onClick={() => addToListMinusBalanceProducts()}>
						{t('add_negative_balance')}
					</button>
					<button className="btn btn-success" tabIndex="-1"
						onClick={() => addToListSelectedProducts()}>
						{t('add_selected')}
					</button>
					<button className="btn btn-primary" tabIndex="-1"
						onClick={() => addToListNotInventoredProducts()}>
						{t('add_all')}
					</button>
				</Modal.Footer>
			</Modal>
			{/* MODAL NOT INVENTORED PRODUCTS */}
		</>
	)
}

export default Index